import { faHighlighter } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { MarkType, Node as ProseMirrorNode } from "prosemirror-model";

import { colors } from "~/ui";
import { NoteHighlightColors } from "~/ui/colors";

import { type KeysArgs, MarkExtension, type MenuEntry } from "../extension";
import { ColourPicker, toggleMark } from "./colour";

import styles from "~/lib/prose/css/highlight.module.css";

// Converts literal colours to enum (backwards-compatibility)
export function getHighlightColourName(originalColour: string) {
	switch (originalColour) {
		case colors.red[200]:
		case colors.red[900]:
		case NoteHighlightColors.red:
			return NoteHighlightColors.red;
		case colors.peach[200]:
		case colors.peach[800]:
		case NoteHighlightColors.peach:
			return NoteHighlightColors.peach;
		case colors.yellow[200]:
		case colors.yellow[800]:
		case NoteHighlightColors.yellow:
			return NoteHighlightColors.yellow;
		case colors.green[200]:
		case colors.green[900]:
		case NoteHighlightColors.green:
			return NoteHighlightColors.green;
		case colors.royal[200]:
		case colors.royal[900]:
		case NoteHighlightColors.royal:
			return NoteHighlightColors.royal;
		case colors.purple[200]:
		case colors.purple[900]:
		case NoteHighlightColors.purple:
			return NoteHighlightColors.purple;
		default:
			return null;
	}
}

export default class HighlightMark extends MarkExtension {
	get name() {
		return "highlight";
	}

	get mark() {
		return {
			parseDOM: [
				{
					tag: "mark",
					getAttrs: (node: HTMLElement) => {
						if (!node.classList.contains(styles.highlight)) return false;

						return { colour: node.getAttribute("data-color") };
					},
				},
				{ tag: "mark" },
			],
			attrs: {
				colour: { default: NoteHighlightColors.yellow }, // it was decided this would be yellow some time ago, see blame
			},
			toDOM(node: ProseMirrorNode) {
				let colourName = node.attrs.colour;

				// Highlight colours used to be stored in literal colours
				if ((node.attrs.colour as string).startsWith("#")) {
					colourName = getHighlightColourName(node.attrs.colour) ?? NoteHighlightColors.yellow;
				}

				return [
					"mark",
					{
						class: cx(styles.highlight, styles[`highlight-${colourName}`]),
						"data-color": colourName,
					},
					0,
				];
			},
		};
	}

	keys({ $type }: KeysArgs) {
		return {
			"Mod-h": toggleMark($type as MarkType, { colour: colors.yellow[200] }),
		};
	}

	menuEntry(): MenuEntry {
		return {
			icon: faHighlighter,
			title: "Highlight color",
			run: (colour: string) => toggleMark(this.$type, { colour }),
			section: "colouring" as const,
			priority: 0,
			colourOptions: Object.values(NoteHighlightColors),
			component: ColourPicker,
		};
	}
}

export class SearchHighlightMark extends MarkExtension {
	get name() {
		return "highlight";
	}

	get mark() {
		return {
			toDOM() {
				return ["mark", { class: "prose-highlight-search" }, 0];
			},
		};
	}
}
